import '@/shared/styles/tailwind.css';
import '@/shared/styles/global.scss';

import { useCallback, useEffect, useMemo } from 'react';
import { AppProps } from 'next/app';
import { appWithTranslation, SSRConfig } from 'next-i18next';

import {
  RuntimeConfigPageProps,
  RuntimeConfigProvider,
  getABSmartlySdkOptions,
} from '@catawiki/next-config/runtime-config';
import { FrontlineAgentSurvey, SurveyProvider } from '@catawiki/survey';
import { captureException } from '@catawiki/observability';
import {
  createI18n,
  ErrorBoundary,
  ErrorPage,
  I18nInstance,
  I18nProvider,
  SnackbarContainer,
} from '@catawiki/cata-style';
import { ABSmartlyWrapper, Units } from '@catawiki/ab-test';

import type { LayoutAssets } from '@/shared/helpers/api/assets';
import { getUserId } from '@/shared/helpers/cookies';
import { Layout } from '@/shared/components/Layout';
import { QueryClientWrapper } from '@/shared/components/QueryClientWrapper';
import { SentryProvider } from '@/shared/components/SentryProvider';
import { queryClient } from '@/shared/helpers/queryClient';
import { sellerProfileQueryParams } from '@/shared/queries/useSellerProfile';
import { useMobileApp } from '@/shared/hooks/useMobileApp';
import { APP_URL } from '@/shared/constants/routes';
import { WebVitalsTracker } from '@/shared/components/WebVitalsTracker/WebVitalsTracker';
import { getSellerRuntimeConfig } from '@/shared/utils/runtimeConfig';

interface SharedProps extends SSRConfig {
  assetsData?: LayoutAssets;
  locale?: string;
  err?: Error;
  units?: Units;
  isMobileApp?: boolean;
  showCookieBanner?: boolean;
  pageType?: string;
}

type AppPageProps = SharedProps & Partial<RuntimeConfigPageProps>;

const getHeaderVariant = (
  pageType?: string
): 'sellerOnly' | 'register' | 'focused' | 'seller' => {
  if (pageType === 'sellerRegister') {
    return 'seller';
  }

  return 'sellerOnly';
};

function MyApp({ Component, pageProps, router }: AppProps<AppPageProps>) {
  const runtimeConfig = pageProps.runtimeConfig ?? getSellerRuntimeConfig();
  const abSmartlySdkOptions = getABSmartlySdkOptions(runtimeConfig, {
    endpoint: 'ENDPOINT',
    apiKey: 'API_KEY',
    environment: 'STAGE',
  });
  const { _nextI18Next } = pageProps;
  const i18n = useMemo(() => {
    const instance = createI18n(router.locale);
    instance.fallbacks = true;

    return instance;
  }, [router.locale]);
  const hideLayout = useMobileApp(pageProps.isMobileApp);
  const userId = pageProps.units?.userId ?? getUserId();

  useEffect(() => {
    if (typeof window !== 'undefined' && userId) {
      window.AB_USER_ID = String(userId);
      // To init Selligent SITE. The init script itself is loaded by GTM.
      window.SELLIGENT_USER_ID = String(userId);
    }

    queryClient.prefetchQuery(sellerProfileQueryParams);
  }, [userId]);

  const getTranslations = useCallback(() => {
    const initialI18nStore: Record<
      string,
      { translations: Record<string, string> }
    > = _nextI18Next?.initialI18nStore || {};

    return Object.entries(initialI18nStore).reduce<
      I18nInstance['translations']
    >((result, [loc, { translations }]) => {
      result[loc] = translations;
      return result;
    }, {});
  }, [_nextI18Next]);

  if (!pageProps?.assetsData || router.route === '/_error') {
    return (
      <RuntimeConfigProvider runtimeConfig={runtimeConfig}>
        <QueryClientWrapper devTools={runtimeConfig.stage === 'development'}>
          <Component {...pageProps} err={pageProps.err} />
        </QueryClientWrapper>
      </RuntimeConfigProvider>
    );
  }

  const layout = (
    <Layout
      data={pageProps.assetsData}
      showCookieBanner={pageProps.showCookieBanner}
      hideFooter={hideLayout}
      hideHeader={hideLayout}
      headerVariant={getHeaderVariant(pageProps.pageType)}
    >
      <Component {...pageProps} />
      <FrontlineAgentSurvey location="seller" />
      <SnackbarContainer />
      <WebVitalsTracker />
    </Layout>
  );

  return (
    <RuntimeConfigProvider runtimeConfig={runtimeConfig}>
      <ErrorBoundary
        fallback={() => <ErrorPage withLogo appUrl={APP_URL} />}
        onError={(error, info) => {
          error.stack = info.componentStack || error.stack;
          captureException(error, {
            extra: {
              componentStack: info.componentStack,
            },
          });
        }}
      >
        <QueryClientWrapper devTools={runtimeConfig.stage === 'development'}>
          <ABSmartlyWrapper
            sdkOptions={abSmartlySdkOptions}
            units={pageProps.units}
            locale={pageProps.locale || 'en'}
          >
            <SurveyProvider>
              <SentryProvider userId={userId} />
              <I18nProvider
                fallback={layout}
                value={i18n}
                onLoad={getTranslations}
              >
                {layout}
              </I18nProvider>
            </SurveyProvider>
          </ABSmartlyWrapper>
        </QueryClientWrapper>
      </ErrorBoundary>
    </RuntimeConfigProvider>
  );
}

export default appWithTranslation(MyApp);
